import { get, post, put, del } from './api';
import { API_ENDPOINTS } from './config';

// 任务状态
export type TaskStatus =
  | 'draft' | 'pending'
  | 'processing' | 'completed' | 'failed';

// 任务信息
export interface Task {
  taskId: string;
  fileId: string;
  folderId?: string;
  title?: string;
  isFavorite?: boolean;
  status: TaskStatus;
  currentStep: number;
  totalSteps: number;
  progressPercentage: number;
  industry?: string;
  company?: string;
  durationSeconds?: number;
  createdAt: string;
  updatedAt?: string;
  speakers?: string[]; // 说话人列表
  summary?: string; // 一句话摘要
  keywords?: string[]; // 关键词列表
}

// 创建任务草稿
export async function createDraft(data: {
  fileId: string;
  industry?: string;
  company?: string;
  context?: string;
  hotwords?: string[];
  scene?: string;
}): Promise<{ taskId: string }> {
  const response = await post<{ taskId: string }>(API_ENDPOINTS.TASK.CREATE_DRAFT, data);
  return response.data;
}

// 查询任务状态
export async function getTaskStatus(taskId: string): Promise<Task> {
  const response = await get<Task>(`${API_ENDPOINTS.TASK.STATUS}/${taskId}`);
  return response.data;
}

// 获取任务列表
export async function getTaskList(): Promise<Task[]> {
  const response = await get<Task[]>(API_ENDPOINTS.TASK.LIST);
  return response.data;
}

// 任务结果数据类型
export interface TranscriptItem {
  text: string;
  speaker: string;
  startTime: number;
  endTime: number;
}

export interface MeetingNoteItem {
  level: number;
  content: string;
  type?: string;
}

export interface ParagraphMatchItem {
  paragraphIndex: number;
  paragraphContent: string;
  paragraphLevel: number;
  matchedUtteranceIndex: number;
  startTime: number;
  endTime: number;
  similarityScore: number;
}

export interface TaskResult {
  taskId: string;
  title?: string;
  fileName: string;
  sourceType: string;
  transcript: TranscriptItem[];
  meetingNotes: MeetingNoteItem[];
  paragraphMatches: ParagraphMatchItem[];
  fileId?: string;
  audioUrl?: string;
  appliedSchemaType?: string;
  appliedSchemaId?: string;
}

// 获取任务结果
export async function getTaskResult(taskId: string): Promise<TaskResult> {
  const response = await get<TaskResult>(`${API_ENDPOINTS.TASK.RESULT}/${taskId}`);
  return response.data;
}

// 获取指定版本的纪要（version=1 为原始生成版）
export async function getNoteByVersion(taskId: string, version: number): Promise<MeetingNoteItem[]> {
  const response = await get<{ content: string }>(`/note/${taskId}/${version}`);
  if (response.data && response.data.content) {
    try {
      return JSON.parse(response.data.content);
    } catch {
      return [];
    }
  }
  return [];
}

// 获取原始生成版纪要（version_type='generated'）
export async function getGeneratedNote(taskId: string): Promise<MeetingNoteItem[]> {
  const response = await get<{ content: string }>(`/note/${taskId}/generated`);
  if (response.data && response.data.content) {
    try {
      return JSON.parse(response.data.content);
    } catch {
      return [];
    }
  }
  return [];
}

// 获取最新编辑版纪要（version_type='edited'）
export async function getEditedNote(taskId: string): Promise<MeetingNoteItem[]> {
  const response = await get<{ content: string }>(`/note/${taskId}/edited`);
  if (response.data && response.data.content) {
    try {
      return JSON.parse(response.data.content);
    } catch {
      return [];
    }
  }
  return [];
}

// 更新收藏状态
export async function updateFavorite(taskId: string, isFavorite: boolean) {
  const response = await put(`/task/${taskId}/favorite`, { isFavorite: isFavorite ? 1 : 0 });
  return response.data;
}

// 获取收藏任务列表
export async function getFavoriteTasks(): Promise<Task[]> {
  const response = await get<Task[]>('/task/favorites');
  return response.data;
}

// 获取最近任务列表
export async function getRecentTasks(): Promise<Task[]> {
  const response = await get<Task[]>('/task/recent');
  return response.data;
}

// 删除任务
export async function deleteTask(taskId: string) {
  const response = await del(`/task/${taskId}`);
  return response.data;
}

// 更新说话人名称（全部）
export async function updateSpeakerName(taskId: string, oldName: string, newName: string) {
  const response = await put(`/task/${taskId}/speaker`, { oldName, newName });
  return response.data;
}

// 更新单条逐字稿的说话人名称
export async function updateSingleSpeakerName(taskId: string, sequenceOrder: number, newName: string) {
  const response = await put(`/task/${taskId}/speaker/single`, { sequenceOrder: String(sequenceOrder), newName });
  return response.data;
}

// 摘要和关键词响应类型
export interface SummaryKeywordsResponse {
  summary: string | null;
  keywords: string[];
}

// 获取任务摘要和关键词
export async function getSummaryKeywords(taskId: string): Promise<SummaryKeywordsResponse> {
  const response = await get<SummaryKeywordsResponse>(`/task/${taskId}/summary-keywords`);
  return response.data;
}

// 更新任务标题
export async function updateTaskTitle(taskId: string, title: string) {
  const response = await put(`/task/${taskId}/title`, { title });
  return response.data;
}

// 更新任务应用的模板
export async function updateAppliedSchema(taskId: string, type: string, schemaId: string) {
  const response = await put(`/task/${taskId}/applied-schema`, { type, schemaId });
  return response.data;
}
